/**
 * GPX-Strecke für den Motiv-Personalisierer · canvArt studios · 05.10.2026
 * ────────────────────────────────────────────────────────────────────────
 * Zeichnet die gelaufene oder gefahrene Strecke aus einer GPX-Datei als
 * Linie auf dieselbe Leinwand, auf der auch Text und Finisher-Block liegen.
 * Danach ist sie ein normales Objekt: verschiebbar, skalierbar, löschbar.
 *
 * Distanz und Datum werden aus derselben Datei gelesen, in die
 * Finisher-Felder geschrieben und direkt mit aufs Motiv gesetzt — ein
 * Upload, fertiges Bild. Wer die Angaben nicht will, leert die Felder.
 *
 * WIE DIESE DATEI AN DEN CANVAS KOMMT
 * ------------------------------------------------------------------
 * Gar nicht über product-personalizer.js — die Datei bleibt unangetastet.
 * Stattdessen derselbe Weg wie in assets/ppz-mobile-fix.js: ein Eingriff
 * in fabric.Canvas.prototype.add. Darin ist `this` die Canvas-Instanz.
 *
 * Als Erkennungszeichen dient der Druckbereich-Rahmen, den der
 * Personalisierer beim Aufbau als allererstes Objekt hinzufügt — ein
 * fabric.Rect mit excludeFromExport: true. Er liefert beides auf einmal:
 * die Canvas-Instanz und die genauen Maße des Druckbereichs. Dieselben
 * Werte, die der Personalisierer intern `area` nennt.
 *
 * Die Hilfslinien beim Verschieben sind zwar auch excludeFromExport,
 * aber vom Typ 'line' — deshalb die Typprüfung auf 'rect'.
 *
 * Beide Eingriffe (hier und in ppz-mobile-fix.js) überschreiben add()
 * verschachtelt und stören sich nicht: jeder ruft die vorige Fassung auf
 * und setzt eine eigene Markierung gegen doppeltes Patchen.
 *
 * EINBAU
 * ------------------------------------------------------------------
 * Nur eine Zeile, in snippets/ppz-finisher-panel.liquid ganz unten:
 *
 *     {% render 'ppz-gpx-panel' %}
 *
 * Das Panel lädt diese Datei selbst. An product-personalizer.js ist
 * nichts zu ändern — weder jetzt noch für künftige Erweiterungen.
 *
 * DATENSCHUTZ
 * ------------------------------------------------------------------
 * Die GPX-Datei verlässt den Browser nicht. Sie wird lokal gelesen und
 * sofort in Bildkoordinaten umgerechnet; hochgeladen wird später nur das
 * fertige Druckbild. Das ist wichtig, weil eine GPX-Aufzeichnung häufig
 * mit der Haustür des Läufers beginnt.
 */
(function () {
  'use strict';

  var MAX_BYTES = 10 * 1024 * 1024; // 10 MB — ein Marathon-Track liegt bei ~1–3 MB
  var MAX_POINTS = 2500;            // reicht für eine saubere Linie, hält den Pfad klein

  /* Gleiche Anfasser-Optik wie die übrigen Objekte im Editor. */
  var OBJECT_STYLE = {
    borderColor: '#1a1a1a',
    cornerColor: '#ffffff',
    cornerStrokeColor: '#1a1a1a',
    cornerStyle: 'circle',
    cornerSize: 16,
    touchCornerSize: 32,
    lockScalingX: false,
    lockScalingY: false,
    lockScalingFlip: true,
    lockSkewingX: true,
    lockSkewingY: true,
    lockRotation: true,
    transparentCorners: false,
    padding: 6,
    borderScaleFactor: 1.5
  };

  var live = { canvas: null, area: null };

  /* ───────── GPX lesen ───────── */

  function parseGpx(text) {
    var doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) {
      throw new Error('Das ist keine lesbare GPX-Datei.');
    }
    var tags = ['trkpt', 'rtept', 'wpt'];
    var nodes = [];
    for (var t = 0; t < tags.length; t++) {
      nodes = doc.getElementsByTagName(tags[t]);
      if (nodes.length > 1) break;
    }
    var pts = [];
    for (var i = 0; i < nodes.length; i++) {
      var lat = parseFloat(nodes[i].getAttribute('lat'));
      var lon = parseFloat(nodes[i].getAttribute('lon'));
      if (isFinite(lat) && isFinite(lon)) pts.push([lat, lon]);
    }
    /* Erster Zeitstempel der Datei. Bei Strava steht er in den Metadaten
       und noch einmal am ersten Trackpunkt; beide meinen den Start. */
    var timeNodes = doc.getElementsByTagName('time');
    var iso = timeNodes.length ? (timeNodes[0].textContent || '') : '';
    return { points: pts, time: iso.replace(/^\s+|\s+$/g, '') };
  }

  /* ───────── Distanz und Datum für die Finisher-Felder ───────── */

  /* Haversine über ALLE Punkte, nicht über die ausgedünnten — sonst
     werden Kurven abgeschnitten und die Strecke fällt zu kurz aus. */
  function distanceKm(pts) {
    var R = 6371.0088;
    var sum = 0;
    for (var i = 1; i < pts.length; i++) {
      var la1 = (pts[i - 1][0] * Math.PI) / 180;
      var la2 = (pts[i][0] * Math.PI) / 180;
      var dLa = la2 - la1;
      var dLo = ((pts[i][1] - pts[i - 1][1]) * Math.PI) / 180;
      var a = Math.sin(dLa / 2) * Math.sin(dLa / 2) +
              Math.cos(la1) * Math.cos(la2) * Math.sin(dLo / 2) * Math.sin(dLo / 2);
      sum += 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
    }
    return sum;
  }

  /* GPS misst 0,5–2 % daneben: Uhren schneiden Kurven, verlieren unter
     Bäumen das Signal und zählen Standzeiten als Zittern mit. Eine
     Marathonuhr zeigt deshalb selten 42,195. Liegt der Messwert nah
     genug an einer bekannten Renndistanz, wird darauf eingerastet —
     sonst steht der gemessene Wert drin. Überschreibbar ist das Feld
     in beiden Fällen. */
  var STANDARD = [
    { km: 5, label: '5 km' },
    { km: 10, label: '10 km' },
    { km: 15, label: '15 km' },
    { km: 21.0975, label: '21,1 km' },
    { km: 42.195, label: '42,195 km' },
    { km: 50, label: '50 km' },
    { km: 100, label: '100 km' }
  ];

  function formatDistance(km) {
    if (!isFinite(km) || km <= 0) return '';
    for (var i = 0; i < STANDARD.length; i++) {
      if (Math.abs(km - STANDARD[i].km) / STANDARD[i].km <= 0.02) {
        return STANDARD[i].label;
      }
    }
    /* Nachkommanullen weg: "7,5 km" statt "7,50 km". */
    var v = (km >= 10 ? km.toFixed(1) : km.toFixed(2))
      .replace(/0+$/, '')
      .replace(/\.$/, '');
    return v.replace('.', ',') + ' km';
  }

  var MONTHS = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.getDate() + '. ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  /* Nur LEERE Felder füllen. Was der Kunde selbst eingetippt hat, wird
     nie überschrieben — auch nicht beim zweiten Upload. */
  function fillFinisher(distanceText, dateText) {
    var done = [];
    [['distance', distanceText], ['date', dateText]].forEach(function (pair) {
      if (!pair[1]) return;
      var el = document.querySelector('[data-ppz-finisher="' + pair[0] + '"]');
      if (!el || (el.value && el.value.replace(/^\s+|\s+$/g, ''))) return;
      el.value = pair[1];
      el.dispatchEvent(new Event('input', { bubbles: true }));
      done.push(pair[1]);
    });
    return done;
  }

  function anyFinisherValue() {
    return Array.prototype.some.call(
      document.querySelectorAll('[data-ppz-finisher]'),
      function (f) { return !!(f.value && f.value.replace(/^\s+|\s+$/g, '')); }
    );
  }

  /* Den vorhandenen Knopf des Personalisierers drücken, statt den
     Textblock selbst zu bauen. Dessen placeFinisher() legt den Block an
     oder aktualisiert einen bestehenden (Marker _ppzFinisher) — damit
     entsteht beim zweiten Upload kein zweiter Block, und ein bereits
     verschobener behält seine Position. */
  function applyFinisher() {
    var btn = document.querySelector('[data-ppz-finisher-apply]');
    if (!btn || !anyFinisherValue()) return false;
    btn.click();
    return true;
  }

  /* Leert der Kunde ALLE Finisher-Felder, soll der Textblock vom Motiv
     verschwinden. Der Personalisierer selbst lässt in dem Fall den
     zuletzt gesetzten Text stehen (in placeFinisher steht
     `if (!content) return;`) — es sähe also so aus, als ließe sich die
     Angabe nicht mehr wegnehmen.

     Die vier Felder sind über das Öffnen und Schließen des Modals
     hinweg dieselben Elemente, die Leinwand nicht. Deshalb einmal
     binden und die jeweils aktuelle Leinwand aus `live` holen. */
  var finisherWatchBound = false;

  function bindFinisherClearWatch() {
    if (finisherWatchBound) return;
    var fields = document.querySelectorAll('[data-ppz-finisher]');
    if (!fields.length) return;
    finisherWatchBound = true;

    Array.prototype.forEach.call(fields, function (field) {
      field.addEventListener('input', function () {
        if (anyFinisherValue()) return;
        var canvas = live.canvas;
        if (!canvas) return;
        var objs = canvas.getObjects();
        for (var i = 0; i < objs.length; i++) {
          /* Marker zuerst; der geht beim Undo verloren, weil toJSON ihn
             nicht mitschreibt. Ersatzweise der Typ: freier Text ist
             i-text, nur der Finisher-Block ist eine textbox. */
          if (objs[i]._ppzFinisher === true || (objs[i].type === 'textbox' && !objs[i]._ppzFreeText)) {
            canvas.remove(objs[i]);
            canvas.discardActiveObject();
            canvas.requestRenderAll();
            break;
          }
        }
      });
    });
  }

  /* Gleichmäßig ausdünnen. Letzter Punkt bleibt erhalten, damit die
     Strecke am Ziel nicht abgeschnitten wirkt. */
  function thin(pts, max) {
    if (pts.length <= max) return pts;
    var step = pts.length / max;
    var out = [];
    for (var i = 0; i < max; i++) out.push(pts[Math.floor(i * step)]);
    out.push(pts[pts.length - 1]);
    return out;
  }

  /* Web-Mercator, BEIDE Achsen im Bogenmaß. Der Längengrad muss mit
     umgerechnet werden: die Hochwertformel liefert Bogenmaß, ein
     Längengrad in Grad daneben macht die Strecke um den Faktor 180/π —
     also rund 57 — zu breit. y negiert, damit Norden oben liegt. */
  function project(pts) {
    var out = [];
    for (var i = 0; i < pts.length; i++) {
      var lat = Math.max(-85, Math.min(85, pts[i][0]));
      out.push([
        (pts[i][1] * Math.PI) / 180,
        -Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))
      ]);
    }
    return out;
  }

  /* Seitenverhältnis-treu in eine Box einpassen und zentrieren. */
  function fit(xy, boxW, boxH) {
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (var i = 0; i < xy.length; i++) {
      if (xy[i][0] < minX) minX = xy[i][0];
      if (xy[i][0] > maxX) maxX = xy[i][0];
      if (xy[i][1] < minY) minY = xy[i][1];
      if (xy[i][1] > maxY) maxY = xy[i][1];
    }
    var w = maxX - minX;
    var h = maxY - minY;
    if (!(w > 0) && !(h > 0)) {
      throw new Error('Die Datei enthält keine Bewegung — alle Punkte liegen aufeinander.');
    }
    var s = Math.min(w > 0 ? boxW / w : Infinity, h > 0 ? boxH / h : Infinity);
    if (!isFinite(s) || s <= 0) s = 1;
    var offX = (boxW - w * s) / 2;
    var offY = (boxH - h * s) / 2;
    var out = [];
    for (var j = 0; j < xy.length; j++) {
      out.push([(xy[j][0] - minX) * s + offX, (xy[j][1] - minY) * s + offY]);
    }
    return out;
  }

  function toPathData(pts) {
    var d = 'M ' + pts[0][0].toFixed(2) + ' ' + pts[0][1].toFixed(2);
    for (var i = 1; i < pts.length; i++) {
      d += ' L ' + pts[i][0].toFixed(2) + ' ' + pts[i][1].toFixed(2);
    }
    return d;
  }

  function assign(target) {
    for (var a = 1; a < arguments.length; a++) {
      var src = arguments[a];
      if (!src) continue;
      for (var k in src) {
        if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
      }
    }
    return target;
  }

  /* ───────── Schatten unter der Linie ───────── */

  /* Ein weicher Schatten hält die Linie auf unruhigen Fotos lesbar.
     Dunkel ist dafür fast immer richtig — auch unter einer weißen oder
     roten Linie. Nur wenn die Linie selbst sehr dunkel ist, bringt ein
     dunkler Schatten nichts; dann kommt ein heller darunter.
     Helligkeit nach Rec. 601, das entspricht dem Seheindruck besser als
     der reine Mittelwert der drei Kanäle. */
  function shadowFor(hex) {
    var c = String(hex || '').replace('#', '');
    if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
    var r = parseInt(c.substr(0, 2), 16);
    var g = parseInt(c.substr(2, 2), 16);
    var b = parseInt(c.substr(4, 2), 16);
    if (!isFinite(r) || !isFinite(g) || !isFinite(b)) { r = 255; g = 255; b = 255; }
    var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return new window.fabric.Shadow({
      color: lum < 0.25 ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)',
      blur: 14,
      offsetX: 0,
      offsetY: 1
    });
  }

  /* ───────── Statuszeile des Personalisierers mitbenutzen ───────── */

  function say(msg, isError) {
    var el = document.querySelector('[data-ppz-status]');
    if (!el) return;
    el.hidden = false;
    el.textContent = msg;
    el.classList.toggle('ppz-status-error', !!isError);
  }

  function quiet() {
    var el = document.querySelector('[data-ppz-status]');
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
    el.classList.remove('ppz-status-error');
  }

  function track(name, payload) {
    try {
      var data = assign({ event: name }, payload || {});
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(data);
      document.dispatchEvent(new CustomEvent('ppz:' + name, { detail: data }));
    } catch (e) {
      /* Tracking darf den Editor nie brechen */
    }
  }

  function protectRoute(object) {
    if (!object || !(object._ppzGpxRoute || object.type === 'path')) return;
    object.set({ lockScalingX: false, lockScalingY: false, lockScalingFlip: true, lockSkewingX: true, lockSkewingY: true, lockRotation: true, touchCornerSize: 32 });
    object.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false, tl: true, tr: true, bl: true, br: true, mtr: false });
  }

  /* ───────── Bedienfeld verdrahten ───────── */

  function wire() {
    var canvas = live.canvas;
    var area = live.area;
    if (!canvas || !area) return;

    /* Vom Dokument aus suchen, nicht vom Wrapper: openModal() hängt das
       Modal beim Öffnen an <body> um, danach liegt das Panel ausserhalb
       des .ppz-wrapper. */
    var input = document.querySelector('[data-ppz-gpx-input]');
    if (!input) return; // Panel auf diesem Produkt nicht eingebunden

    var colorEl = document.querySelector('[data-ppz-gpx-color]');
    var widthEl = document.querySelector('[data-ppz-gpx-width]');
    var nameEl = document.querySelector('[data-ppz-gpx-name]');
    var removeBtn = document.querySelector('[data-ppz-gpx-remove]');

    bindFinisherClearWatch();

    /* Die Strecke wird mit _ppzGpxRoute markiert. Die Undo-Funktion des
       Personalisierers serialisiert den Canvas aber über canvas.toJSON()
       mit einer festen Eigenschaftsliste — eigene Marker sind da nicht
       drin und gehen beim Zurücksetzen verloren. Deshalb zusätzlich der
       Typ: der Personalisierer baut selbst keinen einzigen Pfad (Text ist
       i-text/textbox, Bilder sind image, der Druckrahmen ist rect, die
       Hilfslinien sind line), also ist ein 'path' immer unsere Strecke.
       Wird sie so gefunden, bekommt sie Marker und objectCaching
       gleich wieder gesetzt — Letzteres entscheidet über die Schärfe
       im 300-DPI-Export. */
    function findRoute() {
      var objs = canvas.getObjects();
      for (var i = 0; i < objs.length; i++) {
        var o = objs[i];
        if (o._ppzGpxRoute === true) return o;
        if (o.type === 'path') {
          o._ppzGpxRoute = true;
          o.objectCaching = false;
          return o;
        }
      }
      return null;
    }

    function currentColor() {
      return (colorEl && colorEl.value) || '#FC4C02';
    }

    function currentWidth() {
      var v = widthEl ? parseFloat(widthEl.value) : 3;
      return isFinite(v) && v > 0 ? v : 3;
    }

    function setFileUi(name) {
      var result = document.querySelector('[data-ppz-gpx-result]');
      if (result) result.hidden = !name;
      if (nameEl) {
        nameEl.textContent = name || '';
        nameEl.hidden = !name;
      }
      if (removeBtn) removeBtn.hidden = !name;
    }

    function draw(points) {
      /* Oberes Drittel statt Mitte: bei den canvArt-Motiven sitzt die
         Typografie fast immer unten. Mittig gesetzt landete die Linie
         quer über der Schrift, und der Kunde sah beim Hochladen erst
         einmal Durcheinander. Verschieben und skalieren kann er sie
         danach frei. 70 % der Druckbreite, 42 % der Höhe, Mittelpunkt
         auf 33 % — die Strecke reicht damit von 12 % bis 54 %. */
      var fitted = fit(project(thin(points, MAX_POINTS)), area.w * 0.7, area.h * 0.42);

      var old = findRoute();
      if (old) canvas.remove(old);

      var path = new window.fabric.Path(
        toPathData(fitted),
        assign({}, OBJECT_STYLE, {
          left: area.x + area.w / 2,
          top: area.y + area.h * 0.33,
          originX: 'center',
          originY: 'center',
          fill: 'transparent',
          stroke: currentColor(),
          strokeWidth: currentWidth(),
          strokeLineCap: 'round',
          strokeLineJoin: 'round',
          /* Strichstärke bleibt beim Skalieren gleich — sonst wird die
             Linie beim Vergrößern klobig und beim Verkleinern unsichtbar. */
          strokeUniform: true,
          /* Ohne Caching rendert der Pfad im 300-DPI-Composite scharf
             statt hochskaliert aus einer Bildschirm-Kachel. */
          objectCaching: false,
          /* Weicher Schatten, damit die Linie auch auf unruhigen Fotos
             steht — gleiche Logik wie beim Finisher-Block. */
          shadow: shadowFor(currentColor())
        })
      );
      path._ppzGpxRoute = true;
      protectRoute(path);

      canvas.add(path);
      canvas.setActiveObject(path);
      canvas.requestRenderAll();
      track('personalizer_gpx_added', { gpx_points: points.length });
    }

    input.onclick = function () { input.value = ''; };
    input.onchange = function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > MAX_BYTES) {
        say('Die Datei ist zu groß (höchstens 10 MB).', true);
        input.value = '';
        return;
      }
      if (!/\.gpx$/i.test(file.name)) {
        say('Bitte wähle eine GPX-Datei mit der Endung .gpx.', true);
        input.value = '';
        return;
      }
      var reader = new FileReader();
      reader.onload = function (ev) {
        if (live.canvas !== canvas || !canvas.lowerCanvasEl) return;
        try {
          var parsed = parseGpx(String(ev.target.result));
          var pts = parsed.points;
          if (pts.length < 2) {
            throw new Error('In der Datei stecken keine Streckenpunkte.');
          }

          /* Distanz und Datum stecken in derselben Datei — die muss der
             Kunde nicht noch einmal abtippen. */
          var filled = fillFinisher(
            formatDistance(distanceKm(pts)),
            formatDate(parsed.time)
          );

          /* Reihenfolge mit Absicht: erst der Textblock, dann die
             Strecke. placeFinisher() wählt den Block aus und blendet die
             Statuszeile ab — käme es danach, wäre die Strecke nicht mehr
             ausgewählt und die Meldung unten wieder weg. */
          var placed = applyFinisher();

          draw(pts);
          setFileUi(file.name);

          var modal = input.closest('[data-ppz-modal]');
          var copy = modal ? JSON.parse(modal.dataset.ppzCopy || '{}') : {};
          say(copy.route_success || 'Strecke hinzugefügt. Prüfe jetzt deine Leistungsdaten.');
          var routePanel = input.closest('[data-ppz-choice]');
          if (routePanel) {
            modal.querySelectorAll('[data-ppz-choice]').forEach(function (panel) { if (panel !== routePanel) panel.open = false; });
            routePanel.open = true;
          }

        } catch (err) {
          say(err && err.message ? err.message : 'Die Datei ließ sich nicht lesen.', true);
        }
      };
      reader.onerror = function () {
        say('Die Datei ließ sich nicht lesen. Lade sie in der Dateien-App vollständig herunter und wähle sie erneut aus.', true);
      };
      reader.onloadend = function () { input.value = ''; };
      reader.onabort = function () { say('Auswahl abgebrochen. Bitte wähle die GPX-Datei erneut aus.', true); };
      try { reader.readAsText(file); } catch (_) {
        input.value = '';
        say('Bitte speichere die GPX-Datei zuerst unter Auf meinem iPhone und wähle sie erneut aus.', true);
      }
    };

    if (colorEl) {
      colorEl.oninput = function () {
        var r = findRoute();
        if (!r) return;
        r.set('stroke', currentColor());
        r.set('shadow', shadowFor(currentColor()));
        canvas.requestRenderAll();
      };
    }

    if (widthEl) {
      widthEl.oninput = function () {
        var r = findRoute();
        if (!r) return;
        r.set('strokeWidth', currentWidth());
        canvas.requestRenderAll();
      };
    }

    if (removeBtn) {
      removeBtn.onclick = function () {
        var r = findRoute();
        if (r) {
          canvas.remove(r);
          canvas.discardActiveObject();
          canvas.requestRenderAll();
        }
        setFileUi('');
      };
    }

    /* Die Strecke kann auch über "Auswahl löschen" oder Undo verschwinden.
       Dann muss der Dateiname mit weg, sonst steht da ein Name ohne Linie.
       Nur einmal pro Canvas registrieren — wire() läuft bei jedem Aufbau. */
    if (!canvas.__ppzGpxWatch) {
      canvas.__ppzGpxWatch = true;
      canvas.on('object:added', function (event) { protectRoute(event.target); });
      canvas.getObjects().forEach(protectRoute);
      canvas.on('object:removed', function () {
        if (!findRoute()) setFileUi('');
      });
    }

    /* Beim Wiederöffnen ist der Canvas neu und die Strecke weg —
       dann darf auch der Dateiname nicht mehr dastehen. */
    if (!findRoute()) setFileUi('');
  }

  window.addEventListener('pageshow', function () { if (live.canvas && live.canvas.lowerCanvasEl) wire(); });

  /* ───────── Eingriff in fabric ───────── */

  function patchFabric() {
    var fabric = window.fabric;
    if (!fabric || !fabric.Canvas) return false;
    if (fabric.Canvas.prototype.__ppzGpxPatched) return true;

    var originalAdd = fabric.Canvas.prototype.add;

    fabric.Canvas.prototype.add = function () {
      var self = this;
      var args = Array.prototype.slice.call(arguments);

      for (var i = 0; i < args.length; i++) {
        var obj = args[i];
        /* Der Druckbereich-Rahmen: erstes Objekt beim Canvas-Aufbau,
           liefert Instanz und Maße in einem. */
        if (obj && obj.excludeFromExport === true && obj.type === 'rect') {
          live.canvas = self;
          live.area = {
            x: obj.left,
            y: obj.top,
            w: obj.width,
            h: obj.height
          };
          /* Erst nach dem add verdrahten, damit der Canvas vollständig
             aufgebaut ist. */
          setTimeout(wire, 0);
          break;
        }
      }

      return originalAdd.apply(this, arguments);
    };

    fabric.Canvas.prototype.__ppzGpxPatched = true;
    return true;
  }

  /* fabric.js wird mit defer geladen — kurz pollen statt raten. */
  (function waitForFabric(start) {
    start = start || Date.now();
    if (patchFabric()) return;
    if (Date.now() - start > 12000) return;
    setTimeout(function () {
      waitForFabric(start);
    }, 60);
  })();
})();


